import React, { forwardRef, useImperativeHandle } from "react";
import { showMessage } from "../../../utils/toast/globalToast";
import { _toastVariants, FILE_UPLOAD_DIR } from "../../../utils/Constant";
import {
  formatFileURL,
  uploadFile,
  validateDocumentFileType,
  validateImageFileType,
} from "../../../utils/helper/appHelpers";
import { useFormik } from "formik";
import * as Yup from "yup";

const MyProfileTab = forwardRef(({ user }, ref) => {

  const ServiceSchema = Yup.object().shape({
    fullName: Yup.string().required("Full Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
    profilePhoto: Yup.mixed()
      .nullable()
      .test(
        "fileType",
        "Unsupported image format",
        (value) =>
          !value ||
          ["image/jpeg", "image/png", "image/jpg"].includes(value.type)
      ),
    idProof: Yup.mixed()
      .nullable()
      .test(
        "fileType",
        "Unsupported document type",
        (value) =>
          !value ||
          ["image/jpeg", "image/png", "image/jpg", "application/pdf"].includes(
            value.type
          )
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: user?.userName || "",
      email: user?.email || "",
      mobile: user?.mobileNo || "",
      profilePhoto: user?.profilePic || null, 
      idProof: null, 
    },
    validationSchema: ServiceSchema,
    onSubmit: () => {},
  });

  const { values, errors, touched, setFieldValue } = formik;

  const handleProfilePhotoUpload = (e) => {
    const [file] = e.target.files;
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      return showMessage({
        variant: _toastVariants.Error,
        message: "Profile photo size should not exceed 2 MB",
      });
    }

    if (!validateImageFileType(file.type)) {
      return showMessage({
        variant: _toastVariants.Error,
        message: "Unsupported image file type",
      });
    }

    setFieldValue("profilePhoto", file);
  };

  const handleIdProofUpload = (e) => {
    const [file] = e.target.files;
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      return showMessage({
        variant: _toastVariants.Error,
        message: "File size should not exceed 2 MB",
      });
    }

    if (!validateDocumentFileType(file.type)) {
      return showMessage({
        variant: _toastVariants.Error,
        message: "Unsupported document type",
      });
    }

    setFieldValue("idProof", file);
  };

useImperativeHandle(ref, () => ({
  async submitForm() {
    try {
      let uploadedProfilePhoto = values.profilePhoto;
      let uploadedIdProof = values.idProof;

    if (values.profilePhoto instanceof File) {
      const uploaded = await uploadFile(values.profilePhoto, FILE_UPLOAD_DIR.USER);
      uploadedProfilePhoto =
        typeof uploaded === "string"
          ? uploaded
          : uploaded?.fileUrl || uploaded?.data?.url || null;
      if (!uploadedProfilePhoto && typeof values.profilePhoto === "string") {
        uploadedProfilePhoto = values.profilePhoto;
      }
    } else {
      uploadedProfilePhoto =
        typeof values.profilePhoto === "string" ? values.profilePhoto : null;
    }

    if (values.idProof instanceof File) {
      const uploaded = await uploadFile(values.idProof, FILE_UPLOAD_DIR.USER);
      uploadedIdProof =
        typeof uploaded === "string"
          ? uploaded
          : uploaded?.fileUrl || uploaded?.data?.url || null;
      if (!uploadedIdProof && typeof values.idProof === "string") {
        uploadedIdProof = values.idProof;
      }
    } else {
      uploadedIdProof =
        typeof values.idProof === "string" ? values.idProof : null;
    }

      const finalProfileData = { 
        userName: values.fullName?.trim(),
        email: values.email?.trim(),
        mobileNo: values.mobile?.trim(),
        profilePic: uploadedProfilePhoto,
        idProof: uploadedIdProof,
      };

      return finalProfileData;
    } catch (error) {
      showMessage({
        variant: _toastVariants.Error,
        message:
          "Something went wrong while saving profile. Please try again.",
      });
      return null;
    }
  },

  getValues: () => formik.values,
}));



  return (
    <div className="row mt-4">
      {/* Full Name */}
      <div className="col-md-6">
        <div className="form-group">
          <label>
            Full Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Full Name"
            value={values.fullName}
            onChange={(e) => setFieldValue("fullName", e.target.value)}
          />
          {touched.fullName && errors.fullName && (
            <div className="text-danger">{errors.fullName}</div>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="col-md-6">
        <div className="form-group">
          <label>
            Email ID <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            className="form-control"
            placeholder="Enter Email ID"
            value={values.email}
            onChange={(e) => setFieldValue("email", e.target.value)}
          />
          {touched.email && errors.email && (
            <div className="text-danger">{errors.email}</div>
          )}
        </div>
      </div>

      {/* Mobile Number */}
      <div className="col-md-6">
        <div className="form-group">
          <label>
            Mobile Number <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Mobile Number"
            value={values.mobile}
            onChange={(e) => setFieldValue("mobile", e.target.value)}
          />
          {touched.mobile && errors.mobile && (
            <div className="text-danger">{errors.mobile}</div>
          )}
        </div>
      </div>

      {/* Profile Photo */}
      <div className="col-md-6">
        <div className="form-group">
          <label>Profile Photo</label>
          <input
            type="file"
            id="profilePhotoUpload"
            className="d-none"
            accept="image/*"
            onChange={handleProfilePhotoUpload}
          />
          <label
            htmlFor="profilePhotoUpload"
            className="form-control"
            style={{ cursor: "pointer" }}
          >
            + Upload Photo
          </label>

          {values.profilePhoto && (
            <div className="mt-2 uploaddiv">
              <img
                src={
                  values.profilePhoto instanceof File
                    ? URL.createObjectURL(values.profilePhoto)
                    : formatFileURL(values.profilePhoto)
                }
                alt="Profile"
                className="uploadimages"
              />
              <div
                onClick={() => setFieldValue("profilePhoto", null)}
                className="closeimg"
                style={{ cursor: "pointer" }}
              >
                ✕
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ID Proof */}
      <div className="col-md-6">
        <div className="form-group">
          <label>
            ID Proof (Driving Licence / Passport / Green Card)
            <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            id="idProofUpload"
            className="d-none"
            onChange={handleIdProofUpload}
          />
          <label
            htmlFor="idProofUpload"
            className="form-control"
            style={{ cursor: "pointer" }}
          >
            + Upload ID Proof
          </label>

          {values.idProof && (
            <div className="mt-2 uploaddiv">
              <img
                src={
                  values.idProof instanceof File
                    ? URL.createObjectURL(values.idProof)
                    : formatFileURL(values.idProof)
                }
                alt="ID Proof"
                className="uploadimages"
              />
              <div
                onClick={() => setFieldValue("idProof", null)}
                className="closeimg"
                style={{ cursor: "pointer" }}
              >
                ✕
              </div>
            </div>
          )}

          {touched.idProof && errors.idProof && (
            <div className="text-danger">{errors.idProof}</div>
          )}
        </div>
      </div>
    </div>
  );
});

export default MyProfileTab;
